"use client";

import { ArrowRight, ChevronRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { AuthLink } from "@/components/shared/auth-link";
import { ShimmerButton } from "@/lib/components/shimmer-button";

import { providerLogoUrls } from "@llmgateway/shared/components";

import { AnimatedGroup } from "./animated-group";
import { Navbar } from "./navbar";

import type { Variants } from "@/components/motion-wrapper";
import type { ApiModel, ApiProvider } from "@/lib/fetch-models";
import type { ProviderId } from "@llmgateway/models";

const transitionVariants: { item: Variants } = {
	item: {
		hidden: {
			opacity: 0,
			filter: "blur(12px)",
			y: 12,
		},
		visible: {
			opacity: 1,
			filter: "blur(0px)",
			y: 0,
			transition: {
				type: "spring" as const,
				bounce: 0.3,
				duration: 1.5,
			},
		},
	},
};

// Provider logos configuration
const PROVIDER_LOGOS: { name: string; providerId: ProviderId }[] = [
	{ name: "OpenAI", providerId: "openai" },
	{ name: "Anthropic", providerId: "anthropic" },
	{ name: "Together AI", providerId: "together-ai" },
	{ name: "Groq", providerId: "groq" },
	{ name: "xAI", providerId: "xai" },
	{ name: "DeepSeek", providerId: "deepseek" },
	{ name: "Perplexity", providerId: "perplexity" },
	{ name: "Ai Studio", providerId: "google-ai-studio" },
	{ name: "Moonshot", providerId: "moonshot" },
	{ name: "Novita", providerId: "novita" },
	{ name: "Nebius", providerId: "nebius" },
	{ name: "Zai", providerId: "zai" },
	{ name: "NanoGPT", providerId: "nanogpt" },
	{ name: "Canopywave", providerId: "canopywave" },
	{ name: "AWS Bedrock", providerId: "aws-bedrock" },
	{ name: "Azure", providerId: "azure" },
	{ name: "Inference.net", providerId: "inference.net" },
	{ name: "Mistral", providerId: "mistral" },
	{ name: "Alibaba", providerId: "alibaba" },
	{ name: "ByteDance", providerId: "bytedance" },
	{ name: "Cerebras", providerId: "cerebras" },
	{ name: "Google Vertex", providerId: "google-vertex" },
	{ name: "MiniMax", providerId: "minimax" },
];

interface MigrationData {
	slug: string;
	title: string;
	fromProvider: string;
}

const providerIcons: Record<string, React.ReactNode> = {
	OpenRouter: (
		<svg
			fill="currentColor"
			fillRule="evenodd"
			viewBox="0 0 24 24"
			xmlns="http://www.w3.org/2000/svg"
			className="size-5"
			aria-hidden="true"
		>
			<path d="m16.804 1.957 7.22 4.105v.087L16.73 10.21l.017-2.117-.821-.03c-1.059-.028-1.611.002-2.268.11-1.064.175-2.038.577-3.147 1.352L8.345 11.03c-.284.195-.495.336-.68.455l-.515.322-.397.234.385.23.53.338c.476.314 1.17.796 2.701 1.866 1.11.775 2.083 1.177 3.147 1.352l.3.045c.694.091 1.375.094 2.825.033l.022-2.159 7.22 4.105v.087L16.589 22l.014-1.862-.635.022c-1.386.042-2.137.002-3.138-.162-1.694-.28-3.26-.926-4.881-2.059l-2.158-1.5a21.997 21.997 0 0 0-.755-.498l-.467-.28a55.927 55.927 0 0 0-.76-.43C2.908 14.73.563 14.116 0 14.116V9.888l.14.004c.564-.007 2.91-.622 3.809-1.124l1.016-.58.438-.274c.428-.28 1.072-.726 2.686-1.853 1.621-1.133 3.186-1.78 4.881-2.059 1.152-.19 1.974-.213 3.814-.138z" />
		</svg>
	),
	"Vercel AI Gateway": (
		<svg
			viewBox="0 0 76 65"
			fill="currentColor"
			className="size-5"
			aria-hidden="true"
		>
			<path d="M37.5274 0L75.0548 65H0L37.5274 0Z" />
		</svg>
	),
	LiteLLM: (
		<span className="text-lg" role="img" aria-label="LiteLLM">
			🚅
		</span>
	),
	Portkey: (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 180 180"
			className="size-5"
			aria-hidden="true"
		>
			<path
				fill="url(#portkey-hero-gradient)"
				d="M109.063 7.5c14.782 0 28.37 7.992 35.766 20.851l23.12 40.191.346.614c7.159 12.942 7.078 28.784-.258 41.663l-23.179 40.68c-7.374 12.944-21.01 21.001-35.855 21.001H64.215c-14.95 0-28.669-8.17-36.004-21.26l-22.79-40.68c-7.256-12.951-7.227-28.838.082-41.759l22.738-40.19C35.598 15.604 49.266 7.5 64.156 7.5zM64.156 28.05c-7.392 0-14.312 4.021-18.088 10.696L23.33 78.936c-3.767 6.659-3.783 14.88-.044 21.556l22.797 40.687.178.314c3.803 6.531 10.647 10.457 17.953 10.457h44.788c7.37 0 14.274-3.997 18.057-10.639l23.173-40.681c3.842-6.743 3.825-15.098-.044-21.825l-23.113-40.197c-3.794-6.597-10.674-10.558-18.013-10.558zm25.44 22.11c4.268-3.54 10.597-3.037 14.256 1.172l25.171 28.956.223.263a14.81 14.81 0 0 1-.223 19.16l-25.171 28.957c-3.659 4.209-9.988 4.712-14.255 1.172l-.202-.172c-4.268-3.728-4.71-10.222-.991-14.499L110.284 90l-21.88-25.169c-3.718-4.277-3.277-10.771.99-14.5l.203-.17Z"
			/>
			<defs>
				<linearGradient
					id="portkey-hero-gradient"
					x1="-92.51"
					x2="194.256"
					y1="52.188"
					y2="216.739"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset=".173" stopColor="#00a3ff" />
					<stop offset=".899" stopColor="#ff0f00" />
				</linearGradient>
			</defs>
		</svg>
	),
};

export function Hero({
	navbarOnly,
	sticky = true,
	children,
	migrations = [],
	models,
	providers,
}: {
	navbarOnly?: boolean;
	sticky?: boolean;
	children: React.ReactNode;
	migrations?: MigrationData[];
	models?: ApiModel[];
	providers?: ApiProvider[];
}) {
	return (
		<>
			<Navbar sticky={sticky} models={models} providers={providers}>
				{children}
			</Navbar>
			{!navbarOnly && (
				<main className="overflow-hidden">
					<div
						aria-hidden
						className="z-2 absolute inset-0 pointer-events-none isolate opacity-50 contain-strict hidden lg:block"
					>
						<div className="w-140 h-320 -translate-y-[350px] absolute left-0 top-0 -rotate-45 rounded-full bg-[radial-gradient(68.54%_68.72%_at_55.02%_31.46%,hsla(0,0%,85%,.08)_0,hsla(0,0%,55%,.02)_50%,hsla(0,0%,45%,0)_80%)]" />
						<div className="h-320 absolute left-0 top-0 w-56 -rotate-45 rounded-full bg-[radial-gradient(50%_50%_at_50%_50%,hsla(0,0%,85%,.06)_0,hsla(0,0%,45%,.02)_80%,transparent_100%)] [translate:5%_-50%]" />
						<div className="h-320 -translate-y-[350px] absolute left-0 top-0 w-56 -rotate-45 bg-[radial-gradient(50%_50%_at_50%_50%,hsla(0,0%,85%,.04)_0,hsla(0,0%,45%,.02)_80%,transparent_100%)]" />
					</div>
					<section>
						<div className="relative pt-24 md:pt-36">
							<div
								aria-hidden
								className="absolute inset-0 -z-10 size-full [background:radial-gradient(125%_125%_at_50%_100%,transparent_0%,var(--background)_75%)]"
							/>
							<div className="mx-auto max-w-7xl px-6">
								{/* Centered hero content - optimized for conversion */}
								<div className="text-center max-w-4xl mx-auto">
									<AnimatedGroup variants={transitionVariants}>
										<h1 className="text-balance text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
											LLM Gateway — One API for OpenAI, Anthropic, Google, and
											40+ providers
										</h1>
										<p className="mt-4 md:mt-6 max-w-2xl mx-auto text-balance text-base md:text-lg text-muted-foreground">
											Stop juggling API keys and provider dashboards. Route
											requests across 400+ models, track costs in real-time, and
											switch providers without changing your code.
										</p>
									</AnimatedGroup>

									{/* Primary CTA - Maximum prominence */}
									<AnimatedGroup
										variants={{
											container: {
												visible: {
													transition: {
														staggerChildren: 0.05,
														delayChildren: 0.5,
													},
												},
											},
											...transitionVariants,
										}}
										className="mt-8 md:mt-10 flex flex-col items-center gap-6"
									>
										{/* Primary CTA - ShimmerButton with glow */}
										<div className="relative">
											{/* Outer glow ring */}
											<div className="absolute -inset-3 bg-blue-500/30 rounded-full blur-xl animate-pulse" />
											<AuthLink href="/signup" className="group relative">
												<ShimmerButton
													background="rgb(37, 99, 235)"
													className="shadow-2xl shadow-blue-500/25 px-10 md:px-12 py-3 md:py-4"
												>
													<span className="flex items-center gap-3 text-center text-xl leading-none font-bold tracking-tight whitespace-pre-wrap text-white md:text-2xl">
														<span>Get My API Key</span>
														<ArrowRight className="size-6 md:size-7 transition-transform group-hover:translate-x-1" />
													</span>
												</ShimmerButton>
											</AuthLink>
										</div>

										{/* Trust indicators */}
										<div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
											<span className="flex items-center gap-1.5">
												<svg
													className="size-4 text-green-500"
													fill="currentColor"
													viewBox="0 0 20 20"
													aria-hidden="true"
												>
													<path
														fillRule="evenodd"
														d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
														clipRule="evenodd"
													/>
												</svg>
												Free tier included
											</span>
											<span className="flex items-center gap-1.5">
												<svg
													className="size-4 text-green-500"
													fill="currentColor"
													viewBox="0 0 20 20"
													aria-hidden="true"
												>
													<path
														fillRule="evenodd"
														d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
														clipRule="evenodd"
													/>
												</svg>
												No credit card required
											</span>
											<span className="flex items-center gap-1.5">
												<svg
													className="size-4 text-green-500"
													fill="currentColor"
													viewBox="0 0 20 20"
													aria-hidden="true"
												>
													<path
														fillRule="evenodd"
														d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
														clipRule="evenodd"
													/>
												</svg>
												Setup in 30 seconds
											</span>
										</div>
									</AnimatedGroup>
								</div>
							</div>

							{/* Migration guides section */}
							{migrations.length > 0 && (
								<AnimatedGroup
									variants={{
										container: {
											visible: {
												transition: {
													staggerChildren: 0.05,
													delayChildren: 0.6,
												},
											},
										},
										...transitionVariants,
									}}
								>
									<div className="mx-auto mt-10 max-w-4xl px-6">
										<p className="mb-4 text-center text-sm text-muted-foreground">
											Switching from another provider?
										</p>
										<div className="flex flex-wrap items-center justify-center gap-3">
											{migrations.map((migration) => (
												<Link
													key={migration.slug}
													href={`/migration/${migration.slug}`}
													className="group/card flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm transition-colors hover:border-primary/50 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
												>
													<span className="flex size-6 items-center justify-center text-muted-foreground transition-colors group-hover/card:text-foreground">
														{providerIcons[migration.fromProvider] ?? (
															<ChevronRight
																className="size-4"
																aria-hidden="true"
															/>
														)}
													</span>
													<span className="text-muted-foreground transition-colors group-hover/card:text-foreground">
														{migration.fromProvider}
													</span>
													<ArrowRight
														className="size-3 text-muted-foreground transition-transform group-hover/card:translate-x-0.5 group-hover/card:text-primary"
														aria-hidden="true"
													/>
												</Link>
											))}
											<Link
												href="/migration"
												className="flex items-center gap-1 rounded-full px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
											>
												<span>View all</span>
												<ChevronRight className="size-3" aria-hidden="true" />
											</Link>
										</div>
									</div>
								</AnimatedGroup>
							)}

							<AnimatedGroup
								variants={{
									container: {
										visible: {
											transition: {
												staggerChildren: 0.05,
												delayChildren: 0.75,
											},
										},
									},
									...transitionVariants,
								}}
							>
								<div className="relative -mr-56 mt-8 overflow-hidden px-2 sm:mr-0 sm:mt-12 md:mt-20">
									<div
										aria-hidden
										className="bg-linear-to-b to-background absolute inset-0 z-10 from-transparent from-35%"
									/>
									<div className="inset-shadow-2xs ring-background dark:inset-shadow-white/20 bg-background relative mx-auto max-w-6xl overflow-hidden rounded-2xl border p-4 shadow-lg shadow-zinc-950/15 ring-1">
										<Image
											className="bg-background aspect-15/8 relative hidden rounded-2xl dark:block"
											src="/new-hero.png"
											alt="LLM Gateway dashboard showing analytics and API usage"
											width={2696}
											height={1386}
											priority
										/>
										<Image
											className="z-2 border-border/25 aspect-15/8 relative rounded-2xl border dark:hidden"
											src="/new-hero-light.png"
											alt="LLM Gateway dashboard showing analytics and API usage"
											width={2696}
											height={1386}
											priority
										/>
									</div>
								</div>
							</AnimatedGroup>
						</div>
					</section>
					<section className="bg-background pb-16 pt-16 md:pb-32">
						<div className="group relative m-auto max-w-5xl px-6">
							<div className="absolute inset-0 z-10 flex scale-95 items-center justify-center opacity-0 duration-500 group-hover:scale-100 group-hover:opacity-100">
								<Link
									href="/providers"
									className="block text-sm duration-150 hover:opacity-75"
									prefetch={true}
								>
									<span>View All Providers</span>
									<ChevronRight className="ml-1 inline-block size-3" />
								</Link>
							</div>
							<div className="group-hover:blur-xs mx-auto mt-12 grid max-w-3xl grid-cols-5 gap-x-10 gap-y-6 transition-all duration-500 group-hover:opacity-50 sm:grid-cols-6 sm:gap-x-12 sm:gap-y-10 lg:grid-cols-8">
								{PROVIDER_LOGOS.map((provider) => {
									const LogoComponent = providerLogoUrls[provider.providerId];

									return (
										<div key={provider.name} className="flex">
											{LogoComponent && (
												<LogoComponent className="mx-auto h-16 w-fit object-contain" />
											)}
										</div>
									);
								})}
							</div>
						</div>
					</section>
				</main>
			)}
		</>
	);
}
